import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import Lottie from 'react-lottie';

import { notification } from 'antd';

import {
  Banner, 
  Column, 
  DateAndHour, 
  Dot, 
  Header, 
  PrimaryContainer, 
  Row, 
  StyledTypography, 
  SubscribeContainer,
  ImgContainer
} from './style';
import animationData from '../../../assets/animation/empty-box-animation.json';

import { t } from 'i18next';

import { Live } from '../Live';
import Loading from '../../Loading';
import { LottieContainer, TextLottie } from '../Group/style';
import { ChannelContext, IGroup } from '../../../context/channel';
import { ITalksProps } from '../../../interfaces/university/talk';
import { API_URL } from '../../../settings';
import { storage } from '../../../utils/Storage';
import { subscribeApi } from '../../../api/SubscribeApi';
import BaseModal from '../../../components/shared/BaseModal';
import { SubscriptionForm } from '../../../components/whitelabel/Form';
import { ModalSuccess } from '../../../components/shared/ModalSuccess';
import TopMenu from '../../../components/whitelabel/topMenu/TopMenu';
import { DEFAULT_THUMB_IMAGE } from '../../../components/whitelabel/thumb/Thumb';
import TimeIcon from '../../../components/shared/Icons/TimeIcon';
import Button from '../../../components/shared/button/Button';
import { TalkDescription } from '../../../components/whitelabel/talkDescription/TalkDescription';
import colors from '../../../theme/colors';
import { formatTicketValue } from '../../../utils/helper';
import SectionTalks from '../../../components/whitelabel/sectionTalks/SectionTalks';
import Footer from '../../../components/whitelabel/footer/Footer';
import Page404 from '../../Page404/404';
import { IForm } from '../../../interfaces/university/form';


interface Params {
  channel: string;
  talkSlug: string;
}

interface IPageData {
  type: "talk" | "group" | "event" | "course";
  slug: string;
  username: string;
  ticket_price: number;
  banner?: string;
  form?: IForm;
  title: string;
  is_public: boolean;
}

export const Talk = () => {
  const {data, groups, user} = React.useContext(ChannelContext)
  const { channel, talkSlug} = useParams<Params>();

  const [searchString, setSearchString] = useState<string>('');
  const [screen, setScreen] = useState<"LOADING" | "NOT_FOUND" | "TALK_SUBSCRIBE" | "LIVE" | "GROUP">("LOADING");

  const [selectedTalk, setSelectedTalk] = useState<ITalksProps>();
  const [groupOfTalk, setGroupOfTalk] = useState<IGroup>();
  const [pageData, setPageData] = useState<IPageData>();

  const [formModalVisible, setFormModalVisible] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);

  const [userSubscribed, setUserSubscribed] = useState<string>();
  const [awaitPayment, setAwaitPayment] = useState<string>();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }, []);

  useEffect(() => {
    setScreen("LOADING");
    let wantedTalk: ITalksProps | undefined;
    let wantedGroup: IGroup | undefined;
    
    // get talk and group of talk from context
    groups.forEach(group => {
      const talksOfGroup = group.talks;
      if(talksOfGroup.length > 0){
        const talkFound = talksOfGroup.find(talk => talk.slug === talkSlug);
        if(talkFound){
          wantedGroup = group;
          wantedTalk = talkFound;
        }
      }
    });

    if(wantedTalk && wantedGroup){
      setSelectedTalk(wantedTalk);
      setGroupOfTalk(wantedGroup);
      if(wantedTalk.is_group_only) {
        setPageData({
          type: "group",
          slug: wantedGroup.slug,
          username: wantedGroup.owner_username,
          ticket_price: wantedGroup.ticket_price,
          banner: wantedGroup.banner,
          form: wantedGroup.form,
          is_public: wantedGroup.is_public,
          title: wantedGroup.name,
        });
      }
      else {
        setPageData({
          type: "talk",
          slug: wantedTalk.slug,
          username: wantedTalk.owner_username,
          ticket_price: wantedTalk.ticket_price,
          banner: wantedTalk.banner,
          form: wantedTalk.form,
          title: wantedTalk.title,
          is_public: wantedTalk.is_public,
        });
      }
      setScreen("TALK_SUBSCRIBE");
    }
    else {
      setScreen("NOT_FOUND");
    }

  }, [talkSlug, channel, groups]);

  
  useEffect(() => {
    if(!selectedTalk || !groupOfTalk || !pageData) return;
    setScreen("LOADING");
    
    // if logged user is the webinar owner, redirect to moderation page
    if(user && user.username === selectedTalk.owner_username){
      window.location.href = `${API_URL}/${selectedTalk.owner_username}/${selectedTalk.slug}`;
    }

    // get if user already subscribed or has pending payment
    const subscribedOnGroup = storage.get(`is_subscribed_${groupOfTalk.owner_username}_group_${groupOfTalk.slug}`);
    const subscribedOnTalk = storage.get(`is_subscribed_${selectedTalk.owner_username!}_talk_${selectedTalk.slug}`);
    const storageAwaitPayment = storage.get(`await_payment_${pageData.username}_${pageData.type}_${pageData.slug}`);
    
    const subscribed = (selectedTalk.is_group_only && !selectedTalk.is_public) ? 
    (subscribedOnTalk && subscribedOnGroup) : (subscribedOnTalk || subscribedOnGroup);
    setUserSubscribed(subscribed);

    //if user already subscribed, redirect to live page
    if(subscribed || (pageData.type === "talk" && !selectedTalk.subscription_required)) {
      setScreen("LIVE");
      return;
    }
    else {
      setScreen("TALK_SUBSCRIBE");
    }
    setAwaitPayment(storageAwaitPayment);
    checkAwaitPayment();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTalk, groupOfTalk]);

  useEffect(() => {
    if (!pageData || !selectedTalk) return;

    const userEmail = storage.get(`is_subscribed_${pageData.username}_${pageData.type}_${pageData.slug}`);
    setUserSubscribed(userEmail);

    setUserSubscribed(storage.get(`is_subscribed_${pageData.username}_${pageData.type}_${pageData.slug}`));

    if (showSuccessModal && userEmail) {
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
        if(selectedTalk.is_group_only && !selectedTalk.is_public) {
          checkPrivateEvent(userEmail); 
        }
        else {
          setScreen("LIVE");
        }
        }, 3000); // 3000ms = 3 seconds
    }
  }, [showSuccessModal]);


  const checkPrivateEvent = async (userEmail: string) => {
    if(!pageData || !selectedTalk) return 

    const response = await subscribeApi.subscribe(userEmail, pageData.username, selectedTalk.slug, [], "talk");
    if (typeof response === "string") {
      notification.info({
        message: t('formSubscribe.info.publicGroupPrivateTalk.title'),
        description: t('formSubscribe.info.publicGroupPrivateTalk.description'),
      });
      setUserSubscribed(undefined);
      return;
    }
    else if(response){
      storage.set(`is_subscribed_${pageData.username}_talk_${selectedTalk.slug}`, userEmail);
      setScreen("LIVE");
    }
  }

  const checkAwaitPayment = async () => {
    if(!pageData) return;

    if(!userSubscribed && !awaitPayment && selectedTalk?.ticket_price! > 0 && user) {

      const pendingPayment = await subscribeApi.hasPendingPayment(pageData.type, pageData.slug, pageData.username);
      if(pendingPayment){
        storage.set(`await_payment_${pageData.username}_${pageData.type}_${pageData.slug}`, user.email);
        setAwaitPayment(user.email);
      }
    }
  }

  const handleRedirectToLive = async () => {
    if(!pageData || !selectedTalk) return;
    setScreen("LOADING");

    if(awaitPayment) {
      // check if payment is ok to continue
      const response = await subscribeApi.guessUserInfo(awaitPayment, pageData.username, pageData.slug, pageData.type);
      if(response){
        const userEmail = storage.get(`await_payment_${pageData.username}_${pageData.type}_${pageData.slug}`);
        storage.remove(`await_payment_${pageData.username}_${pageData.type}_${pageData.slug}`);
        // if the payment of group is ok, but the webinar is private
        if(selectedTalk.is_group_only && !selectedTalk.is_public) {
          checkPrivateEvent(userEmail!); 
        }
        else {
          storage.set(`is_subscribed_${pageData.username}_${pageData.type}_${pageData.slug}`,  awaitPayment);
          setScreen("LIVE");
        }
      }
      else {
        notification.info({message: t('talk.awaitPayment')});
        setScreen("TALK_SUBSCRIBE");
      }
    }
    else if(userSubscribed){
      setScreen("LIVE");
    }
    else {
      setScreen("TALK_SUBSCRIBE");
      setFormModalVisible(true);
    }
  }

  const defaultOptions = {
    loop: true,
    autoplay: true, 
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };

  
  if(screen === "LOADING") return <Loading/>;
  if(screen === "NOT_FOUND") return <Page404/>;
  if(screen === "LIVE") return <Live userSubscribedKey={`is_subscribed_${pageData?.username}_${pageData?.type}_${pageData?.slug}`} email={userSubscribed!} group={groupOfTalk} talk={selectedTalk!} />;
  
  return (
        (pageData && selectedTalk && groupOfTalk) ? (
        <div>
          <Helmet>
            <title>{`Webinar - ${selectedTalk.title}`}</title>
            <meta property="og:title" content={`Webinars - ${selectedTalk.title}`} />
            <meta property="og:description" content={selectedTalk.description}/>  
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={`Webinars - ${selectedTalk.title}`} />
            <meta property="og:image" content={data.images.logo} />
            <meta property="og:image:alt" content={selectedTalk.description} />
          </Helmet>

          <BaseModal
            onCancel={() => setFormModalVisible(false)}
            footer={null} 
            title={t('form.subscribe')!} 
            setToggleModal={setFormModalVisible} 
            toggleModal={formModalVisible}
            >
            <SubscriptionForm
              setToggleModal={setFormModalVisible} 
              data={data} 
              is_group_only={selectedTalk.is_group_only}
              groupName={groupOfTalk.name}
              slug={pageData.slug}
              userName={pageData.username}
              fields={pageData?.form?.fields || []}
              type={pageData.type}
              ticket_price={pageData.ticket_price}
              setSuccessModal={setShowSuccessModal}
              is_public={pageData.is_public}
            />
          </BaseModal>

          <ModalSuccess title={t('form.subscribe')!} text={t('formSubscribe.success')} setToggleModal={setShowSuccessModal} toggleModal={showSuccessModal} />

          <TopMenu
            trilhaConteudo
            transparent={false}
            logoLightMode={data.images.logo}
            searchString={searchString}
            setSearchString={setSearchString}
          />

          { pageData.banner && <Banner src={pageData.banner} alt={t('talk.banner.alt', {webinar: pageData.title})!}/> }

          <Header>
            <Row>
              <PrimaryContainer>
                <ImgContainer>
                  <img src={selectedTalk.cover || DEFAULT_THUMB_IMAGE} alt={`cover ${selectedTalk.title}`} />
                </ImgContainer> 
                <Column>
                  <StyledTypography className='title' color='title' type='heading4'>{selectedTalk.title}</StyledTypography>
                  <Row>
                    <StyledTypography color='label' type='body2'>{t('group')}</StyledTypography>
                    <Dot />
                    <StyledTypography color='label' type='body2'>{groupOfTalk.name}</StyledTypography> 
                  </Row> 
                  <Row>
                    <TimeIcon style={{marginRight: '5px'}} fill={selectedTalk.state === "recorded" ? data.config.colors.colorPrimary : colors.primary}/>
                    <DateAndHour>
                      {selectedTalk.date.split("às")[0] + " " + t('date.at') + " " + selectedTalk.date.split("às")[1]}
                    </DateAndHour>
                  </Row>
                </Column>
                <SubscribeContainer>
                  {
                    (awaitPayment || !selectedTalk.is_public) ? ( 
                      <Button
                      onClick={handleRedirectToLive}
                      size = 'medium'
                      enable
                      typographyType='subheading3'
                      style={{borderRadius: '30px'}}
                      customBackground = {data.config.colors.colorPrimary  ? data.config.colors.colorPrimary : colors.secondary}
                    >
                      {t('talk.enter').toUpperCase()}
                    </Button>) : ( 
                     <Button
                     onClick={() => {setFormModalVisible(true)}}
                     size = 'medium'
                     enable
                     typographyType='subheading3'
                     style={{borderRadius: '30px', minWidth: '170px'}}
                     customBackground = {data.config.colors.colorPrimary  ? 
                      data.config.colors.colorPrimary : 
                      colors.secondary}
                   >
                     {t('subscribe').toUpperCase()}
                   </Button> 
                    )
                  }
                  {
                    (pageData.ticket_price && pageData.ticket_price !== 0 && !userSubscribed && !awaitPayment) ? 
                      (<StyledTypography color='label' type='subheading3'> {
                        formatTicketValue(pageData.ticket_price) 
                      } </StyledTypography>)
                      : ("")
                  }
                </SubscribeContainer>
              </PrimaryContainer>
            </Row>
            
            <TalkDescription object={selectedTalk} />

          </Header>

        {(groupOfTalk.talks.filter(t => t.slug !== selectedTalk.slug && t.is_public).length > 0) &&
          <SectionTalks
            key={1}
            color={data.config.colors.colorPrimary}
            title={t('talk.moreOfthisGroup')!}
            colorBackground={data.config.colors.colorSectionTalks ? data.config.colors.colorSectionTalks : colors.offWhite }
            talks={groupOfTalk.talks.filter(t => t.slug !== selectedTalk.slug && t.is_public)}
            slug={groupOfTalk.slug}
            apiUrl={API_URL}
            wrap={data.config.wrapTalks ? data.config.wrapTalks :false}
            channel={channel}
            relationed={true}
            group_is_public={groupOfTalk.is_public}
            user_is_subscribed={userSubscribed !== undefined}
          />
        }

        <Footer
          companyName={data.name}
          image={data.footer.logo ? data.footer.logo : data.images.logo}
          color={data ? data.footer.color : colors.black}
          textColor={data ? data.footer.footerTextColor : colors.black}
          social={data.footer.social}
        />
      </div>
      ) : ( <Page404/> )
    );
}
